import React, { useContext, useState, useMemo, useEffect } from 'react';
import {
    Button,
    TextField,
    Box,
    Typography,
    IconButton,
    Link,
    CircularProgress,
    MenuItem,
    Select,
    FormControl,
    Checkbox,
    ListItemText,
} from '@mui/material';
import StoreContext from '../../../react-core-lib/components/app/store-context';
import { useNavigate, useLocation } from 'react-router-dom';
import { Auth } from 'aws-amplify';
import config from '../../../config';
import UserApi from '../../../rest-api/user-api';
import { ReactComponent as CetraLogo } from '../../../assets/icons/CetraLogo.svg';
import { ReactComponent as LogoCircle } from '../../../assets/icons/LogoCircle.svg';
import { ReactComponent as EyeOffIcon } from '../../../assets/icons/EyeOff.svg';
import ConfirmationDialog from '../../UIControls/ConfirmationDialog';
import { registerStyles } from './styles/onboard-register.styles';

const buildQueryString = (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return qs ? `?${qs}` : '';
};

const occupationOptions = [
    { label: 'Physiotherapist', value: 'Physiotherapist', mixpanelTag: 'Physiotherapist' },
    { label: 'Chiropractor', value: 'Chiropractor', mixpanelTag: 'Chiropractor' },
    { label: 'Osteopath', value: 'Osteopath', mixpanelTag: 'Osteopath' },
    { label: 'Sports Therapist', value: 'Sports Therapist', mixpanelTag: 'Sports Therapist' },
    { label: 'Occupational Therapist', value: 'Occupational Therapist', mixpanelTag: 'Occupational Therapist' },
    {
        label: 'First Contact Practitioner',
        value: 'First Contact Practitioner',
        mixpanelTag: 'First Contact Practitioner',
    },
    { label: 'GP / Doctor / Consultant', value: 'GP / Doctor / Consultant', mixpanelTag: 'GP - Doctor - Consultant' },
    {
        label: 'Clinical Educator / Lecturer / Clinical Researcher',
        value: 'Clinical Educator / Lecturer / Clinical Researcher',
        mixpanelTag: 'Clinical Educator - Lecturer - Clinical Researcher',
    },
    { label: 'Receptionist / Admin', value: 'Receptionist / Admin', mixpanelTag: 'Receptionist - Admin' },
    { label: 'Student', value: 'Student', mixpanelTag: 'Student' },
    { label: 'Other', value: 'Other', mixpanelTag: 'Other' },
];

const occupationLabelByValue = occupationOptions.reduce((acc, option) => {
    acc[option.value] = option.label;
    return acc;
}, {});

const getSignUpErrorMessage = (error) => {
    const message = error?.message || '';

    if (message.toLowerCase().includes('username should be an email')) {
        return 'Invalid email address';
    }

    return message || 'An error occurred. Please try again.';
};

const OnboardRegisterScreen = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { authStore } = useContext(StoreContext);

    const queryParams = new URLSearchParams(location.search);
    const inviteId = queryParams.get('inviteId');
    const senderId = queryParams.get('senderId');
    const isShowOffer = queryParams.get('LVS') === 'true';
    const isInviteFlow = Boolean(inviteId && senderId);

    const [userData, setUserData] = useState({
        firstName: '',
        lastName: '',
        emailAddress: '',
        occupation: [],
        userDescription: [],
        password: '',
        subscriptionData: {
            accountType: 'Free',
            trialActive: false,
            trialEndDate: null,
            cancelled: false,
            subscriptionEndDate: null,
            stripeCustomerId: null,
        },
    });
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);
    const [inviteData, setInviteData] = useState(null);

    useEffect(() => {
        const fetchInviteData = async () => {
            if (!isInviteFlow) return;

            try {
                const fetchedInviteData = await UserApi.getClinicianDataMultiuserNoAuth(inviteId);
                fetchedInviteData.inviteId = inviteId;
                fetchedInviteData.senderId = senderId;

                setInviteData(fetchedInviteData);
                console.warn('Fetched invite data:', fetchedInviteData);

                if (fetchedInviteData.invitedEmail) {
                    setUserData((prevUserData) => ({
                        ...prevUserData,
                        emailAddress: fetchedInviteData.invitedEmail.toLowerCase(),
                    }));
                }
            } catch (error) {
                console.error('Error fetching invite data:', error);
            }
        };

        fetchInviteData();
    }, [inviteId, senderId, isInviteFlow]);

    // Password validation requirements
    const passwordRequirements = [
        { key: 'length', test: (pwd) => pwd.length >= 8, text: 'Use at least 8 characters' },
        {
            key: 'case',
            test: (pwd) => /[A-Z]/.test(pwd) && /[a-z]/.test(pwd),
            text: 'Use upper and lower case letters (e.g. Aa)',
        },
        { key: 'number', test: (pwd) => /\d/.test(pwd), text: 'Use a number (e.g. 1234)' },
        { key: 'symbol', test: (pwd) => /[!@#$%^&*(),.?":{}|<>]/.test(pwd), text: 'Use a symbol (e.g. !@#$)' },
    ];

    const passwordCriteria = passwordRequirements.map((req) => ({
        ...req,
        meets: req.test(userData.password || ''),
    }));

    const hasStartedTypingPassword = userData.password && userData.password.length > 0;
    const passwordsMatch = useMemo(
        () => userData.password && confirmPassword && userData.password === confirmPassword,
        [userData.password, confirmPassword],
    );
    const passwordValid = useMemo(() => passwordCriteria.every((criterion) => criterion.meets), [passwordCriteria]);
    const isEmailValid = userData.emailAddress && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(userData.emailAddress);

    const canSubmit = useMemo(
        () => userData.firstName && userData.lastName && isEmailValid && passwordValid && passwordsMatch,
        [userData.firstName, userData.lastName, isEmailValid, passwordValid, passwordsMatch],
    );

    const handleInputChange = (event) => {
        const { name, value } = event.target;
        // Normalize email to lowercase to prevent case-sensitivity issues with Cognito
        const normalizedValue = name === 'emailAddress' ? value.toLowerCase() : value;
        setUserData((prev) => ({ ...prev, [name]: normalizedValue }));
    };

    const handleOccupationChange = (event) => {
        const { value } = event.target;
        const selectedValues = typeof value === 'string' ? value.split(',') : value;
        setUserData((prev) => ({
            ...prev,
            occupation: selectedValues,
            userDescription: selectedValues,
        }));
    };

    const handleTogglePasswordVisibility = () => {
        setShowPassword((prev) => !prev);
    };

    const handleConfirmRedirect = () => {
        navigate('/onboard/login');
    };

    const handleSignUp = async () => {
        if (!canSubmit) {
            setError('Please fill in all required fields correctly.');
            return;
        }

        setIsLoading(true);
        setError('');

        // Normalize email to lowercase to prevent case-sensitivity issues with Cognito
        const normalizedEmail = userData.emailAddress.toLowerCase();

        try {
            const result = await authStore.signUp(normalizedEmail, userData.password);
            localStorage.setItem('userData', JSON.stringify(userData));

            if (result.success) {
                const qs = buildQueryString({
                    ...(isInviteFlow ? { inviteId, senderId } : {}),
                    ...(isShowOffer ? { LVS: 'true' } : {}),
                });
                navigate(`/onboard/code${qs}`, { state: { userData } });
            } else {
                setError(result.message);
            }
        } catch (error) {
            console.error('Error during sign up:', error);
            if (
                error.message === 'An account with the given email already exists.' ||
                error.message === 'PreSignUp failed with error Internal Server Error.'
            ) {
                setError(
                    'Sorry, we are unable to process your request at this time. Please contact us at hello@cetrahealth.com for further assistance.',
                );
            } else if (error.message === 'User already exists') {
                try {
                    await Auth.resendSignUp(normalizedEmail);
                    authStore.setTempPassword(userData.password);
                    const qs = buildQueryString({
                        ...(isInviteFlow ? { inviteId, senderId } : {}),
                        ...(isShowOffer ? { LVS: 'true' } : {}),
                    });
                    navigate(`/onboard/code${qs}`, { state: { userData } });
                } catch (resendError) {
                    if (resendError.message.includes('User is already confirmed')) {
                        setShowConfirmationDialog(true);
                    } else {
                        setError('Failed to resend verification code. Please try again or contact support.');
                    }
                }
            } else {
                setError(getSignUpErrorMessage(error));
            }
        } finally {
            setIsLoading(false);
        }
    };

    const getValidationDotStyle = (criterion) => {
        if (!hasStartedTypingPassword) {
            return registerStyles.validationDotDefault;
        }
        return criterion.meets ? registerStyles.validationDotSuccess : registerStyles.validationDotError;
    };

    const styles = registerStyles;

    return (
        <Box sx={styles.pageContainer}>
            {/* Logo */}
            <Box sx={styles.logo}>
                <Box sx={{ position: 'relative', ...styles.logoSize }}>
                    <Box component={LogoCircle} sx={styles.logoCircle} />
                    <Box component={CetraLogo} sx={styles.logoIcon} />
                </Box>
            </Box>

            {/* Registration Card */}
            <Box sx={styles.card}>
                <Typography sx={styles.title}>Create an account</Typography>
                <Typography sx={styles.subtitle}>
                    Already have an account?{' '}
                    <Link component="button" onClick={() => navigate('/onboard/login')} sx={styles.loginLink}>
                        Log in
                    </Link>
                </Typography>

                {/* First Name / Last Name Row */}
                <Box sx={styles.rowContainer}>
                    <Box sx={styles.fieldContainer}>
                        <Typography sx={styles.label}>First name</Typography>
                        <TextField
                            name="firstName"
                            value={userData.firstName}
                            onChange={handleInputChange}
                            fullWidth
                            sx={styles.input}
                            placeholder=""
                        />
                    </Box>
                    <Box sx={styles.fieldContainer}>
                        <Typography sx={styles.label}>Last name</Typography>
                        <TextField
                            name="lastName"
                            value={userData.lastName}
                            onChange={handleInputChange}
                            fullWidth
                            sx={styles.input}
                            placeholder=""
                        />
                    </Box>
                </Box>

                {/* Email Address */}
                <Box sx={{ marginBottom: 2 }}>
                    <Typography sx={styles.label}>Email address</Typography>
                    <TextField
                        name="emailAddress"
                        type="email"
                        value={userData.emailAddress}
                        onChange={handleInputChange}
                        fullWidth
                        sx={styles.input}
                        error={userData.emailAddress.length > 0 && !isEmailValid}
                        InputProps={{
                            readOnly: !!inviteData?.invitedEmail,
                        }}
                        placeholder=""
                    />
                </Box>

                {/* Occupation (optional) */}
                <Box sx={{ marginBottom: 2 }}>
                    <Typography sx={styles.label}>
                        Occupation <span style={{ color: 'rgba(97, 96, 99, 0.5)' }}>(optional)</span>
                    </Typography>
                    <FormControl fullWidth>
                        <Select
                            multiple
                            value={userData.occupation}
                            onChange={handleOccupationChange}
                            displayEmpty
                            renderValue={(selected) => {
                                const selectedValues = Array.isArray(selected) ? selected : selected ? [selected] : [];
                                if (selectedValues.length === 0) {
                                    return <em style={{ color: 'rgba(97, 96, 99, 0.5)' }}>Select occupation</em>;
                                }
                                return selectedValues.map((value) => occupationLabelByValue[value] || value).join(', ');
                            }}
                            sx={styles.selectInput}
                        >
                            {occupationOptions.map((option) => (
                                <MenuItem key={option.value} value={option.value} sx={styles.occupationMenuItem}>
                                    <Checkbox checked={userData.occupation.includes(option.value)} />
                                    <ListItemText
                                        primary={option.label}
                                        primaryTypographyProps={{ sx: styles.occupationMenuText }}
                                    />
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>

                {/* Password / Confirm Password Row */}
                <Box sx={styles.rowContainer}>
                    <Box sx={styles.fieldContainer}>
                        <Typography sx={styles.label}>Password</Typography>
                        <TextField
                            name="password"
                            type={showPassword ? 'text' : 'password'}
                            value={userData.password}
                            onChange={handleInputChange}
                            fullWidth
                            sx={styles.input}
                            placeholder=""
                        />
                    </Box>
                    <Box sx={styles.fieldContainer}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography sx={{ ...styles.label, display: { xs: 'none', md: 'block' } }}>
                                Confirm password
                            </Typography>
                            <Typography sx={{ ...styles.label, display: { xs: 'block', md: 'none' } }}>
                                Confirm password
                            </Typography>
                            <IconButton
                                onClick={handleTogglePasswordVisibility}
                                sx={{ padding: 0, marginBottom: 0.5 }}
                                aria-label={showPassword ? 'Hide password' : 'Show password'}
                                disableRipple
                            >
                                <EyeOffIcon style={{ width: 19, height: 16, opacity: showPassword ? 0.5 : 1 }} />
                            </IconButton>
                        </Box>
                        <TextField
                            type={showPassword ? 'text' : 'password'}
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            fullWidth
                            sx={styles.input}
                            error={confirmPassword.length > 0 && !passwordsMatch}
                            helperText={confirmPassword.length > 0 && !passwordsMatch ? 'Passwords do not match.' : ''}
                            placeholder=""
                        />
                    </Box>
                </Box>

                {/* Password Validation */}
                <Box sx={styles.validationContainer}>
                    {passwordCriteria.map((criterion) => (
                        <Box key={criterion.key} sx={styles.validationItem}>
                            <Box
                                sx={{
                                    ...styles.validationDot,
                                    ...getValidationDotStyle(criterion),
                                }}
                            />
                            <Typography sx={styles.validationText}>{criterion.text}</Typography>
                        </Box>
                    ))}
                </Box>

                {/* Error Message */}
                {error && (
                    <Typography sx={{ color: '#F44336', fontSize: '14px', textAlign: 'center', marginBottom: 2 }}>
                        {error}
                    </Typography>
                )}

                {/* Continue Button */}
                <Button
                    variant="contained"
                    fullWidth
                    onClick={handleSignUp}
                    disabled={!canSubmit || isLoading}
                    sx={styles.continueButton}
                >
                    {isLoading ? <CircularProgress size={24} color="inherit" /> : 'Continue'}
                </Button>

                {/* Terms Text */}
                <Typography sx={styles.termsText}>
                    By continuing, you agree to the{' '}
                    <Link href={config.tsCsUrl} target="_blank" sx={styles.termsLink}>
                        Terms of use
                    </Link>{' '}
                    and{' '}
                    <Link href={config.privacyPolicyUrl} target="_blank" sx={styles.termsLink}>
                        Privacy policy
                    </Link>
                </Typography>
            </Box>

            <ConfirmationDialog
                open={showConfirmationDialog}
                handleClose={() => setShowConfirmationDialog(false)}
                onConfirm={handleConfirmRedirect}
                title="Account already exists!"
                message="An account with this email already exists. You will be redirected to the login screen. Please use the forgot password link to reset your password if necessary."
                confirmText="Proceed to log in"
                cancelTextReq={false}
            />
        </Box>
    );
};

export default OnboardRegisterScreen;
